import React, { useMemo, useState } from 'react';
import { getKeystoneDescriptor } from '../../data/keystoneCards';
import './SkillTreeMap.css';

const MAP_PAD = 64;
const NODE_GAP_X = 172;
const NODE_GAP_Y = 112;
const NODE_R = 22;

const NODE_CONTAINER_KEYS = ['nodes', 'children', 'items'];
const TRACK_CONTAINER_KEYS = ['tracks', 'tiers', 'levels', 'branches'];
const PREREQ_KEYS = ['prerequisites', 'prereqs', 'requires', 'required', 'parents', 'parent_ids', 'depends_on'];
const DISPLAY_STATUSES = new Set([
  'locked-by-level',
  'pursuable',
  'unlockable-now',
  'unlocked',
  'locked-by-foreign-keystone',
]);

const HANDLER_STYLE = {
  card_unlock: { color: '#fbbf24', soft: 'rgba(251, 191, 36, 0.18)', label: 'Card' },
  passive_modifier: { color: '#34d399', soft: 'rgba(52, 211, 153, 0.16)', label: 'Passive' },
  xp_tag: { color: '#60a5fa', soft: 'rgba(96, 165, 250, 0.16)', label: 'XP' },
  keystone_marker: { color: '#f87171', soft: 'rgba(248, 113, 113, 0.16)', label: 'Keystone' },
  default: { color: '#cbd5e1', soft: 'rgba(148, 163, 184, 0.14)', label: 'Node' },
};

function asArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function nodeId(node) {
  const value = node?.node_id ?? node?.id ?? node?.slug;
  return value == null || value === '' ? null : String(value);
}

function payloadHandler(node) {
  const payload = node?.payload;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return node.payload_handler || payload.handler || payload.payload_handler || payload.type || null;
  }
  return node?.payload_handler || null;
}

function payloadData(node) {
  const payload = node?.payload;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
      return payload.data;
    }
    const copy = { ...payload };
    delete copy.handler;
    delete copy.payload_handler;
    delete copy.type;
    return copy;
  }
  return node?.payload_data && typeof node.payload_data === 'object' ? node.payload_data : {};
}

function labelFor(node) {
  return (
    node.label ||
    node.title ||
    node.name ||
    node.short_label ||
    nodeId(node) ||
    'Skill'
  );
}

function splitLabel(label, max = 18) {
  const words = String(label || '').split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [String(label || '').slice(0, max)];
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > max && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, 2);
}

function unwrapDefinition(treeDefinition) {
  if (!treeDefinition || typeof treeDefinition !== 'object') return {};
  return treeDefinition.tree_definition || treeDefinition.treeDefinition || treeDefinition.definition || treeDefinition;
}

function collectNodes(value, context = {}, out = []) {
  if (!value || typeof value !== 'object') return out;
  if (Array.isArray(value)) {
    value.forEach((item, index) => collectNodes(item, { ...context, siblingIndex: index }, out));
    return out;
  }

  const id = nodeId(value);
  const nextTrack = value.track_id || value.trackId || value.track || context.trackId;
  const nextDepth = Number.isFinite(Number(value.tier ?? value.level ?? value.depth))
    ? Number(value.tier ?? value.level ?? value.depth)
    : Number(context.depth || 0);

  if (id) {
    out.push({
      ...value,
      id,
      trackId: nextTrack ? String(nextTrack) : 'core',
      depth: nextDepth,
      payloadHandler: payloadHandler(value),
      payloadData: payloadData(value),
      label: labelFor(value),
      _order: out.length,
    });
  }

  [...TRACK_CONTAINER_KEYS, ...NODE_CONTAINER_KEYS].forEach((key) => {
    if (!(key in value)) return;
    const childContext = {
      trackId: nextTrack || (id && key !== 'children' ? id : context.trackId),
      depth: nextDepth + (key === 'children' || key === 'nodes' || key === 'items' ? 1 : 0),
    };
    collectNodes(value[key], childContext, out);
  });
  return out;
}

function collectExplicitEdges(value, out = []) {
  if (!value || typeof value !== 'object') return out;
  if (Array.isArray(value)) {
    value.forEach((item) => collectExplicitEdges(item, out));
    return out;
  }
  if (Array.isArray(value.edges)) {
    value.edges.forEach((edge) => {
      if (!edge || typeof edge !== 'object') return;
      const from = edge.from || edge.source || edge.parent || edge.start;
      const to = edge.to || edge.target || edge.child || edge.end;
      if (from && to) out.push({ from: String(from), to: String(to), type: edge.type || 'explicit' });
    });
  }
  [...TRACK_CONTAINER_KEYS, ...NODE_CONTAINER_KEYS].forEach((key) => collectExplicitEdges(value[key], out));
  return out;
}

function prereqNodeIds(node) {
  const ids = [];
  PREREQ_KEYS.forEach((key) => {
    asArray(node[key]).forEach((value) => {
      if (typeof value === 'string' || typeof value === 'number') {
        ids.push(String(value));
      } else if (value && typeof value === 'object') {
        const direct = value.node_id || value.id || value.slug || value.unlocked || value.node;
        if (direct) ids.push(String(direct));
      }
    });
  });
  return ids;
}

function normalizeEdges(definition, nodes) {
  const knownIds = new Set(nodes.map((node) => node.id));
  const edgeKey = new Set();
  const edges = [];
  const add = (from, to, type) => {
    if (!knownIds.has(from) || !knownIds.has(to) || from === to) return;
    const key = `${from}->${to}`;
    if (edgeKey.has(key)) return;
    edgeKey.add(key);
    edges.push({ from, to, type });
  };

  collectExplicitEdges(definition).forEach((edge) => add(edge.from, edge.to, edge.type));
  nodes.forEach((node) => {
    prereqNodeIds(node).forEach((from) => add(from, node.id, 'prerequisite'));
  });
  return edges;
}

function progressSets(userProgress = {}) {
  const unlocked = new Set(asArray(userProgress.unlocked_nodes || userProgress.unlocks || userProgress.skill_tree_unlocks).map(String));
  const active = new Set(asArray(userProgress.active_nodes).map(String));
  return { unlocked, active };
}

function normalizeStatus(status) {
  const value = String(status || '').replace(/_/g, '-');
  if (DISPLAY_STATUSES.has(value)) return value;
  if (value === 'active') return 'pursuable';
  if (value === 'locked') return 'locked-by-level';
  return value || 'locked-by-level';
}

function nodeStateFor(nodeIdValue, userProgress = {}) {
  const states = userProgress.node_states || userProgress.nodeStates || userProgress.states || {};
  return states[nodeIdValue] || null;
}

function layoutNodes(nodes) {
  const tiers = new Map();
  nodes.forEach((node) => {
    const tier = Number.isFinite(Number(node.depth)) ? Number(node.depth) : 0;
    if (!tiers.has(tier)) tiers.set(tier, []);
    tiers.get(tier).push(node);
  });

  const tierValues = [...tiers.keys()];
  const maxTier = Math.max(0, ...tierValues);
  const widestTierCount = Math.max(1, ...[...tiers.values()].map((tierNodes) => tierNodes.length));
  const slotCounts = new Map();

  return nodes.map((node) => {
    const rawX = node.x ?? node.grid_x ?? node.position?.x;
    const rawY = node.y ?? node.grid_y ?? node.position?.y;
    if (Number.isFinite(Number(rawX)) && Number.isFinite(Number(rawY))) {
      return { ...node, x: Number(rawX), y: Number(rawY) };
    }

    const tier = Number.isFinite(Number(node.depth)) ? Number(node.depth) : 0;
    const slot = slotCounts.get(tier) || 0;
    slotCounts.set(tier, slot + 1);
    const tierCount = tiers.get(tier)?.length || 1;
    const maxRowWidth = (widestTierCount - 1) * NODE_GAP_X;
    const rowWidth = (tierCount - 1) * NODE_GAP_X;
    const rowInset = (maxRowWidth - rowWidth) / 2;

    return {
      ...node,
      x: MAP_PAD + rowInset + slot * NODE_GAP_X,
      y: MAP_PAD + (maxTier - tier) * NODE_GAP_Y,
    };
  });
}

function nodeStatus(node, userProgress) {
  const { unlocked, active } = progressSets(userProgress);
  const state = nodeStateFor(node.id, userProgress);
  if (state?.status) return normalizeStatus(state.status);
  if (node.status) return normalizeStatus(node.status);
  if (active.has(node.id)) return 'pursuable';
  if (unlocked.has(node.id)) return 'unlocked';
  return 'locked-by-level';
}

function styleFor(node, status) {
  const base = HANDLER_STYLE[node.payloadHandler] || HANDLER_STYLE.default;
  if (status === 'locked-by-level') {
    return { ...base, color: '#64748b', soft: 'rgba(100, 116, 139, 0.12)' };
  }
  if (status === 'locked-by-foreign-keystone') {
    return { ...base, color: '#475569', soft: 'rgba(71, 85, 105, 0.13)' };
  }
  if (status === 'pursuable') {
    return { ...base, color: '#22d3ee', soft: 'rgba(34, 211, 238, 0.18)' };
  }
  if (status === 'unlockable-now') {
    return { ...base, color: '#facc15', soft: 'rgba(250, 204, 21, 0.22)' };
  }
  return base;
}

function edgePath(from, to) {
  const sign = to.y >= from.y ? 1 : -1;
  const dy = Math.max(44, Math.abs(to.y - from.y) * 0.44);
  const c1y = from.y + sign * dy;
  const c2y = to.y - sign * dy;
  return `M ${from.x} ${from.y} C ${from.x} ${c1y}, ${to.x} ${c2y}, ${to.x} ${to.y}`;
}

export function normalizeSkillTreeDefinition(treeDefinition, userProgress = {}) {
  const definition = unwrapDefinition(treeDefinition);
  const nodes = layoutNodes(collectNodes(definition));
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const edges = normalizeEdges(definition, nodes)
    .map((edge) => ({ ...edge, fromNode: nodeById.get(edge.from), toNode: nodeById.get(edge.to) }))
    .filter((edge) => edge.fromNode && edge.toNode);
  const width = Math.max(520, Math.max(0, ...nodes.map((node) => node.x)) + MAP_PAD);
  const height = Math.max(300, Math.max(0, ...nodes.map((node) => node.y)) + MAP_PAD);

  return {
    title: definition.title || definition.name || treeDefinition?.version || 'Skill Tree',
    version: definition.version || treeDefinition?.version || 'preview',
    nodes: nodes.map((node) => {
      const state = nodeStateFor(node.id, userProgress);
      return {
        ...node,
        status: nodeStatus(node, userProgress),
        nodeState: state,
        progress: state?.progress || node.progress || null,
      };
    }),
    edges,
    width,
    height,
  };
}

export function SkillTreeCardUnlockCelebration({ payload = {}, visible = true }) {
  if (!visible) return null;
  const name = payload.card_name || payload.name || payload.card_slug || payload.slug || payload.card_id || 'New card';
  return (
    <div className="stm-card-unlock" data-testid="skill-tree-card-unlock" role="status">
      <div className="stm-card-unlock__shine" />
      <div className="stm-card-unlock__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="6" y="3" width="12" height="18" rx="2" />
          <path d="M9 8h6M9 12h6M9 16h3" />
        </svg>
      </div>
      <div>
        <div className="stm-card-unlock__kicker">Card acquired</div>
        <div className="stm-card-unlock__name">{name}</div>
      </div>
    </div>
  );
}

export function SkillTreePassiveModifierBadge({ payload = {}, active = true }) {
  const modifier = payload.modifier && typeof payload.modifier === 'object' ? payload.modifier : payload;
  const label = modifier.label || modifier.name || modifier.id || 'Passive active';
  const value = modifier.value || modifier.amount || modifier.description || null;
  return (
    <div className={`stm-passive-badge${active ? ' stm-passive-badge--active' : ''}`} data-testid="skill-tree-passive-badge">
      <span className="stm-passive-badge__dot" />
      <span className="stm-passive-badge__label">{label}</span>
      {value && <span className="stm-passive-badge__value">{value}</span>}
    </div>
  );
}

export function SkillTreeXpFloater({ payload = {}, visible = true }) {
  if (!visible) return null;
  const amount = payload.amount || payload.xp || payload.xp_value || payload.value || 1;
  const tags = asArray(payload.tags || payload.tag).join(' / ');
  return (
    <div className="stm-xp-floater" data-testid="skill-tree-xp-floater" role="status">
      <span className="stm-xp-floater__amount">+{amount} XP</span>
      {tags && <span className="stm-xp-floater__tags">{tags}</span>}
    </div>
  );
}

export function SkillTreeKeystoneMarker({ payload = {} }) {
  const slug = payload.keystone_slug || payload.slug || payload.id || 'keystone';
  const keystone = getKeystoneDescriptor(slug);
  const label = payload.label || keystone?.genericName || slug;
  const progress = Math.max(0, Math.min(100, Number(payload.progress_pct ?? payload.progress ?? 0)));
  const accent = payload.accent || keystone?.accent || '#f87171';
  return (
    <div className="stm-keystone-marker" style={{ '--stm-keystone-accent': accent }} data-testid="skill-tree-keystone-marker">
      <div className="stm-keystone-marker__ring" aria-hidden="true">
        <span style={{ transform: `scaleX(${progress / 100})` }} />
      </div>
      <div className="stm-keystone-marker__copy">
        <span>{label}</span>
        <strong>{Math.round(progress)}%</strong>
      </div>
    </div>
  );
}

export function SkillTreePayloadSurface({ handler, payload = {}, visible = true }) {
  if (handler === 'card_unlock') return <SkillTreeCardUnlockCelebration payload={payload} visible={visible} />;
  if (handler === 'passive_modifier') return <SkillTreePassiveModifierBadge payload={payload} active={visible} />;
  if (handler === 'xp_tag') return <SkillTreeXpFloater payload={payload} visible={visible} />;
  if (handler === 'keystone_marker') return <SkillTreeKeystoneMarker payload={payload} />;
  return null;
}

function SkillTreeNode({ node, selected, onSelect }) {
  const style = styleFor(node, node.status);
  const lines = splitLabel(node.label);
  const handlerLabel = (HANDLER_STYLE[node.payloadHandler] || HANDLER_STYLE.default).label;
  const progress = node.progress || {};
  const progressTarget = Math.max(1, Number(progress.target || 0));
  const progressCurrent = Math.max(0, Number(progress.current || 0));
  const progressPct = Math.min(1, progressCurrent / progressTarget);
  const showProgress = node.status === 'pursuable';
  const labelY = showProgress ? NODE_R + 34 : NODE_R + 18;

  return (
    <g
      className={`stm-node stm-node--${node.status}${selected ? ' stm-node--selected' : ''}`}
      transform={`translate(${node.x}, ${node.y})`}
      onClick={() => onSelect(node)}
      role="button"
      tabIndex={0}
      aria-label={`${node.label} ${node.status}`}
      data-testid={`skill-tree-node-${node.id}`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onSelect(node);
      }}
    >
      {node.status === 'unlockable-now' && (
        <circle className="stm-node__pulse" r={NODE_R + 11} fill="none" stroke={style.color} strokeWidth="1.5" />
      )}
      <circle r={NODE_R + 8} fill={style.soft} opacity={selected ? 1 : 0.72} />
      <circle r={NODE_R} fill="rgba(15, 23, 42, 0.94)" stroke={style.color} strokeWidth={selected ? 2.4 : 1.6} />
      {['pursuable', 'unlockable-now', 'unlocked'].includes(node.status) && (
        <circle r={NODE_R * 0.45} fill={style.color} opacity={0.16} />
      )}
      {node.status === 'unlocked' && (
        <path d="M-7 0 L-2 6 L8 -7" fill="none" stroke={style.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {node.status === 'pursuable' && <circle r="5" fill={style.color} />}
      {node.status === 'locked-by-foreign-keystone' && (
        <path
          className="stm-node__lock"
          d="M-7 -1v-5a7 7 0 0 1 14 0v5M-9 -1h18v13H-9z"
          fill="none"
          stroke={style.color}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {showProgress && (
        <g className="stm-node__progress" transform={`translate(${-NODE_R}, ${NODE_R + 7})`}>
          <rect x="0" y="0" width={NODE_R * 2} height="5" rx="2.5" />
          <rect x="0" y="0" width={NODE_R * 2 * progressPct} height="5" rx="2.5" className="stm-node__progress-fill" />
          <text x={NODE_R} y="16" textAnchor="middle">{progressCurrent}/{progressTarget}</text>
        </g>
      )}
      <text className="stm-node__label" y={labelY} textAnchor="middle">
        {lines.map((line, index) => (
          <tspan key={line} x="0" dy={index === 0 ? 0 : 12}>{line}</tspan>
        ))}
      </text>
      <text className="stm-node__handler" y={-NODE_R - 10} textAnchor="middle" fill={style.color}>
        {handlerLabel}
      </text>
    </g>
  );
}

export default function SkillTreeMap({ treeDefinition, userProgress, onNodeSelect, height = 520 }) {
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const graph = useMemo(
    () => normalizeSkillTreeDefinition(treeDefinition, userProgress),
    [treeDefinition, userProgress],
  );
  const selected = graph.nodes.find((node) => node.id === selectedId) || graph.nodes[0] || null;
  const nodeById = useMemo(() => new Map(graph.nodes.map((node) => [node.id, node])), [graph.nodes]);
  const selectedDescription = selected?.status === 'locked-by-foreign-keystone'
    ? 'Locked by another equipped keystone.'
    : (selected?.description || selected?.summary || 'No description supplied.');
  const showSelectedProgress = selected?.progress && selected?.status !== 'locked-by-foreign-keystone';

  const handleSelect = (node) => {
    setSelectedId(node.id);
    if (onNodeSelect) onNodeSelect(node);
  };

  if (!graph.nodes.length) {
    return (
      <div className="stm-empty" style={{ minHeight: height }} data-testid="skill-tree-empty">
        <span className="stm-empty__title">Skill Tree</span>
        <span className="stm-empty__sub">No nodes in this definition.</span>
      </div>
    );
  }

  return (
    <div className="stm-shell" data-testid="skill-tree-map">
      <div className="stm-toolbar">
        <div>
          <h2>{graph.title}</h2>
          <span>{graph.version}</span>
        </div>
        <div className="stm-zoom" aria-label="Map zoom controls">
          <button type="button" onClick={() => setZoom((value) => Math.max(0.7, Number((value - 0.1).toFixed(2))))} aria-label="Zoom out">-</button>
          <strong>{Math.round(zoom * 100)}%</strong>
          <button type="button" onClick={() => setZoom((value) => Math.min(1.5, Number((value + 0.1).toFixed(2))))} aria-label="Zoom in">+</button>
        </div>
      </div>

      <div className="stm-content">
        <div className="stm-viewport" style={{ height }}>
          <svg
            className="stm-svg"
            viewBox={`0 0 ${graph.width} ${graph.height}`}
            width={graph.width * zoom}
            height={graph.height * zoom}
            role="img"
            aria-label={`${graph.title} skill tree graph`}
          >
            <defs>
              <marker id="stm-arrow" markerWidth="8" markerHeight="8" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L0,6 L6,3 z" fill="rgba(148, 163, 184, 0.72)" />
              </marker>
            </defs>
            <g className="stm-edges">
              {graph.edges.map((edge) => {
                const from = nodeById.get(edge.from);
                const to = nodeById.get(edge.to);
                return (
                  <path
                    key={`${edge.from}-${edge.to}`}
                    d={edgePath(from, to)}
                    fill="none"
                    markerEnd="url(#stm-arrow)"
                    data-testid={`skill-tree-edge-${edge.from}-${edge.to}`}
                  />
                );
              })}
            </g>
            <g>
              {graph.nodes.map((node) => (
                <SkillTreeNode
                  key={node.id}
                  node={node}
                  selected={selected?.id === node.id}
                  onSelect={handleSelect}
                />
              ))}
            </g>
          </svg>
        </div>

        <aside className="stm-detail" data-testid="skill-tree-detail">
          {selected && (
            <>
              <div className="stm-detail__status">{selected.status}</div>
              <h3>{selected.label}</h3>
              <p>{selectedDescription}</p>
              {showSelectedProgress && (
                <div className="stm-detail__progress">
                  <div>
                    <span>{selected.progress.criterion_key || 'progress'}</span>
                    <strong>{selected.progress.current}/{Math.max(1, Number(selected.progress.target || 0))}</strong>
                  </div>
                  <div className="stm-detail__progress-track">
                    <span
                      style={{
                        transform: `scaleX(${Math.min(1, Number(selected.progress.current || 0) / Math.max(1, Number(selected.progress.target || 0)))})`,
                      }}
                    />
                  </div>
                </div>
              )}
              <SkillTreePayloadSurface
                handler={selected.payloadHandler}
                payload={selected.payloadData}
                visible={selected.status === 'unlocked'}
              />
              {prereqNodeIds(selected).length > 0 && (
                <div className="stm-detail__prereqs">
                  <span>Requires</span>
                  {prereqNodeIds(selected).map((id) => <code key={id}>{id}</code>)}
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
